import React, { useMemo, useState } from 'react';

import type Konva from 'konva';
import { merge } from 'lodash';
import { Layer, Group, Rect } from 'react-konva';

import type { TStageSize } from '@vlabs/monokit/shared/ui/canvas/adaptive-stage';
import { AdaptiveStage } from '@vlabs/monokit/shared/ui/canvas/adaptive-stage';
import { ANCHOR_PADDING } from '@vlabs/monokit/shared/ui/canvas/adaptive-stage/constants';

import { DEFAULT_STYLES } from './constants';
import type { TBbox, TBboxStyle } from './types';

export interface BboxRendererProps {
  originSize?: TStageSize;
  bboxList?: TBbox[];
  onBBoxClick?: (stack: TBbox[] | null) => void;
  hoveredBboxId?: string | null;
  onBBoxHover?: (bbox: TBbox | null) => void;
  dimOutsideSelected?: boolean;
}

type TRenderableBbox = TBbox & { $brId: string; style: TBboxStyle };

function BboxRenderer({
  originSize = undefined,
  bboxList = [],
  onBBoxClick = undefined,
  hoveredBboxId = undefined,
  onBBoxHover = undefined,
  dimOutsideSelected = false,
}: BboxRendererProps): React.JSX.Element {
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [stageSize, setStageSize] = useState<TStageSize | undefined>(undefined);

  // единый масштаб нужен и для bbox, и для области затемнения
  const stageScale = useMemo(() => {
    if (!originSize?.width || !originSize.height || !stageSize) return 1;

    return Math.min(
      stageSize.width / originSize.width,
      stageSize.height / originSize.height,
    );
  }, [originSize, stageSize]);

  const renderableBboxList: TRenderableBbox[] = useMemo(() => {
    let $bboxList = bboxList.map((box, idx) => ({
      ...box,
      $brId: String(idx),
      style: merge({}, DEFAULT_STYLES, box.style),
    }));
    if (originSize?.width && originSize.height && stageSize) {
      $bboxList = $bboxList.map((bbox) => ({
        ...bbox,
        x: bbox.x * stageScale,
        y: bbox.y * stageScale,
        width: bbox.width * stageScale,
        height: bbox.height * stageScale,
      }));
    }
    return $bboxList;
  }, [bboxList, originSize, stageScale, stageSize]);

  const renderedStageSize = useMemo(() => {
    if (!stageSize) return undefined;
    if (!originSize?.width || !originSize.height) return stageSize;

    return {
      width: originSize.width * stageScale,
      height: originSize.height * stageScale,
    };
  }, [originSize, stageScale, stageSize]);

  const selectedBboxes = renderableBboxList.filter((bbox) => bbox.isSelected);
  const shouldDimOutsideSelected = Boolean(
    dimOutsideSelected && selectedBboxes.length !== 0 && renderedStageSize,
  );

  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!onBBoxClick && !onBBoxHover) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const hits = stage.getAllIntersections(stage.getPointerPosition());

    // top node под курсором
    const nodeTop = hits?.[0];

    if (!nodeTop) {
      if (hoverId !== null) setHoverId(null);
      onBBoxHover?.(null);
      stage.container().style.cursor = onBBoxClick ? 'default' : 'inherit';
      return;
    }

    const { $brId } = nodeTop.attrs;
    if ($brId !== hoverId) {
      setHoverId($brId);
      onBBoxHover?.(bboxList[Number($brId)] ?? null);
    }
    stage.container().style.cursor = onBBoxClick ? 'pointer' : 'inherit';
  };

  const handleMouseLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    setHoverId(null);
    onBBoxHover?.(null);
    const stage = e.target.getStage();
    if (!stage) return;
    stage.container().style.cursor = 'default';
  };

  const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!onBBoxClick) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) {
      onBBoxClick(null);
      return;
    }

    const hitNodes = stage.getAllIntersections(pos);
    const stack: TBbox[] = [];
    hitNodes.forEach((node) => {
      const nodeId = node.attrs.$brId;
      if (!nodeId) return;
      stack.push((bboxList[Number(nodeId)] as TBbox));
    });
    onBBoxClick(stack);
  };

  return (
    <AdaptiveStage
      onChangeSize={setStageSize}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      originSize={originSize}
    >
      <Layer listening>
        {shouldDimOutsideSelected ? (
          <Group listening={false}>
            {/* маска затемняет кадр, а selected bbox вырезаются из неё */}
            <Rect
              fill="rgba(184, 191, 205, 0.5)"
              height={renderedStageSize.height + ANCHOR_PADDING * 2}
              width={renderedStageSize.width + ANCHOR_PADDING * 2}
              x={-ANCHOR_PADDING}
              y={-ANCHOR_PADDING}
            />
            {selectedBboxes.map((bbox) => (
              <Rect
                {...bbox}
                fill="black"
                globalCompositeOperation="destination-out"
                key={bbox.$brId}
              />
            ))}
          </Group>
        ) : null}
        <Group listening>
          {renderableBboxList.map((box) => {
            // внешний hover связывает bbox с элементом управления за пределами холста
            const isHover = box.$brId === hoverId || box.id === hoveredBboxId;
            let state = 'base';
            if (box.isSelected) state = 'selected';
            else if (isHover) state = 'hover';

            return (
              <Rect
                {...box}
                id={box.$brId}
                key={box.$brId}
                {...box.style[state]}
              />
            );
          })}
        </Group>
      </Layer>
    </AdaptiveStage>
  );
}

export { BboxRenderer };
